import { type FC } from 'react';
import { PiChatCircle } from 'react-icons/pi';
import {
  FunnelInteractionDirection,
  FunnelInteractionType,
} from 'src/api/generated';
import type { ConversationCardInteractionProps } from 'src/modules/communications/types/conversation-card.type';
import { CallStatusIndicator } from 'src/modules/shared/components/atoms/indicators/call-status-indicator';
import { DirectionIndicator } from 'src/modules/shared/components/atoms/indicators/direction-indicator';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useMediaQuery } from 'src/modules/shared/hooks/use-media-query';

/** Renders the compact interaction preview used by the regular card. */
export const ConversationCardInteraction: FC<
  ConversationCardInteractionProps
> = ({ interaction, className = '' }) => {
  const showDesktopTooltip = useMediaQuery('(min-width: 640px)');
  const heightClass = 'h-[17px]';

  if (!interaction) {
    return <div className={`${heightClass} ${className}`} aria-hidden="true" />;
  }

  const incomingDirection =
    interaction.direction === FunnelInteractionDirection.INCOMING ? (
      <DirectionIndicator isOutbound={false} variant="whatsapp" size="xs" />
    ) : null;

  if (interaction.modelType === FunnelInteractionType.CALL) {
    return (
      <div
        className={`flex min-w-0 w-full items-center gap-1.5 ${heightClass} ${className}`}
      >
        {incomingDirection}
        <CallStatusIndicator
          status={interaction.status}
          size="xs"
          className="[&_svg]:!text-current [&_p]:!leading-[17px] text-secondary-600"
        />
      </div>
    );
  }

  const hasLongText =
    typeof interaction.snippet === 'string' && interaction.snippet.length > 80;
  const previewText = interaction.snippet ?? '';

  const messagePreview = (
    <Text
      as="div"
      size="tiny"
      color="secondary"
      disableAutoLink
      className="min-w-0 w-full truncate text-xs leading-[17px] text-secondary-600"
      title={previewText}
    >
      <span className="mr-1.5 inline-flex h-[17px] items-center gap-1.5 align-top">
        {incomingDirection}
        <PiChatCircle
          className="h-3.5 w-3.5 text-secondary-600"
          aria-hidden="true"
        />
      </span>
      {previewText}
    </Text>
  );

  return (
    <div className={`relative min-w-0 w-full ${heightClass} ${className}`}>
      {hasLongText && showDesktopTooltip ? (
        <Tooltip
          content={`${previewText.slice(0, 150)}${
            previewText.length > 150 ? '…' : ''
          }`}
          size="sm"
          placement="top"
          className="max-w-[24rem]"
          contentClassName="min-w-0 w-full"
          showDelay={1}
        >
          {messagePreview}
        </Tooltip>
      ) : (
        messagePreview
      )}
    </div>
  );
};
